import Quill from 'quill/core';
import Header from './formats/Header';
import listenBody from './utils/listenBody';
import safeParseUrl from '../utils/url/safeParseUrl';
import getHeaderCollapse from './utils/getHeaderCollapse';

const Module = Quill.import('core/module');

const safeQuerySelector = (element: Element, selector: string) => {
  try {
    return element.querySelector(selector);
  } catch (err) {
    return null;
  }
};

class ScrollToAnchor extends Module {
  constructor(quill: Quill, options: Record<string, unknown>) {
    super(quill, options);

    listenBody(
      'click',
      (e) => {
        // Modifier keys won't trigger in-page scroll. For example, on macOS:
        // * metaKey opens the link in a new tab.
        // * shiftKey opens the link in a new window.
        // * ctrlKey triggers a context menu.
        // * altKey downloads the link.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

        const closestAnchor =
          e.target instanceof HTMLElement && e.target.closest('a');
        if (
          !closestAnchor ||
          closestAnchor.target === '_blank' ||
          closestAnchor.isContentEditable
        ) {
          return;
        }

        const url = safeParseUrl(closestAnchor.href);
        if (
          url?.hash &&
          url.protocol === window.location.protocol &&
          url.host === window.location.host &&
          url.pathname === window.location.pathname
        ) {
          this.scrollToAnchor(url.hash);
        }
      },
      quill,
    );
  }

  private scrollToHeader(header: HTMLElement) {
    // Scroll to the header directly when it's visible.
    if (header.offsetParent) {
      header.scrollIntoView();
      return;
    }

    // Otherwise, scroll to the nearest visible parent header.
    const allHeaders: { domNode: HTMLHeadingElement }[] =
      getHeaderCollapse(this.quill)?.getCollapsedHeaders() ?? [];

    const visibleHeaders = allHeaders.filter(
      ({ domNode }) => domNode.offsetParent,
    );

    const nearestVisibleHeader = visibleHeaders.reverse().filter(
      (visibleHeader) =>
        header.compareDocumentPosition(visibleHeader.domNode) & // eslint-disable-line no-bitwise
        Node.DOCUMENT_POSITION_PRECEDING,
    )[0];

    nearestVisibleHeader?.domNode?.scrollIntoView();
  }

  scrollToAnchor(hash: string) {
    let decoded: string | undefined;
    try {
      decoded = decodeURIComponent(hash);
    } catch (error) {}

    if (!decoded) return;

    const matchedHeader = safeQuerySelector(this.quill.root, decoded);
    if (matchedHeader instanceof HTMLElement) {
      this.scrollToHeader(matchedHeader);
    } else {
      const header = safeQuerySelector(
        this.quill.root,
        Header.getSelectors(decoded.slice(1)).join(','),
      );

      if (header instanceof HTMLElement) {
        this.scrollToHeader(header);
      }
    }
  }
}

export default ScrollToAnchor;
